import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Coins, CheckCircle, Sparkle, Lightning, CreditCard, XCircle } from "@phosphor-icons/react";

export default function Credits() {
    const [packs, setPacks] = useState([]);
    const [balance, setBalance] = useState(0);
    const [busy, setBusy] = useState(null);
    const [polling, setPolling] = useState(false);
    const [ar, setAr] = useState({ auto_recharge: { enabled: false, pack_key: "starter", threshold_cents: 500 }, card: null, has_card: false });
    const [arBusy, setArBusy] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const loadAll = () => {
        api.get("/credits/packs").then((r) => setPacks(r.data.packs || [])).catch(() => {});
        api.get("/credits/balance").then((r) => setBalance(r.data.credits_cents || 0)).catch(() => {});
        api.get("/credits/auto-recharge").then((r) => setAr(r.data)).catch(() => {});
    };

    useEffect(() => { loadAll(); }, []);

    // Handle return from Stripe
    useEffect(() => {
        const paid = searchParams.get("credits_paid");
        const cancelled = searchParams.get("credits_cancelled");
        const arSetup = searchParams.get("autorecharge_setup");
        const arCancelled = searchParams.get("autorecharge_cancelled");
        if (cancelled) {
            toast.info("Purchase cancelled — no charge.");
            searchParams.delete("credits_cancelled");
            setSearchParams(searchParams, { replace: true });
            return;
        }
        if (arCancelled) {
            toast.info("Card setup cancelled.");
            searchParams.delete("autorecharge_cancelled");
            setSearchParams(searchParams, { replace: true });
            return;
        }
        if (arSetup) {
            setPolling(true);
            let stop = false;
            let attempts = 0;
            const tick = async () => {
                if (stop) return;
                attempts += 1;
                try {
                    const { data } = await api.get(`/credits/auto-recharge/setup-status/${arSetup}`);
                    if (data.setup_completed) {
                        toast.success(`Card saved — ${data.card_summary?.brand?.toUpperCase()} ••${data.card_summary?.last4}`);
                        setPolling(false);
                        searchParams.delete("autorecharge_setup");
                        setSearchParams(searchParams, { replace: true });
                        loadAll();
                        return;
                    }
                    if (attempts >= 20) { setPolling(false); return; }
                    setTimeout(tick, 2000);
                } catch (e) {
                    setPolling(false);
                    toast.error(formatApiError(e));
                }
            };
            tick();
            return () => { stop = true; };
        }
        if (!paid) return;
        setPolling(true);
        let stop = false;
        let attempts = 0;
        const tick = async () => {
            if (stop) return;
            attempts += 1;
            try {
                const { data } = await api.get(`/credits/buy-status/${paid}`);
                if (data.credits_applied) {
                    toast.success(`Credits added — new balance $${((data.new_balance_cents || 0) / 100).toFixed(2)}`);
                    setPolling(false);
                    searchParams.delete("credits_paid");
                    setSearchParams(searchParams, { replace: true });
                    loadAll();
                    return;
                }
                if (data.payment_status === "expired") {
                    toast.error("Checkout session expired — please try again.");
                    setPolling(false);
                    searchParams.delete("credits_paid");
                    setSearchParams(searchParams, { replace: true });
                    return;
                }
                if (attempts >= 30) {
                    toast.warning("Still confirming — refresh in a moment.");
                    setPolling(false);
                    return;
                }
                setTimeout(tick, 2000);
            } catch (e) {
                setPolling(false);
                toast.error(formatApiError(e));
            }
        };
        tick();
        return () => { stop = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("credits_paid"), searchParams.get("credits_cancelled")]);

    const buy = async (packKey) => {
        setBusy(packKey);
        try {
            const { data } = await api.post("/credits/buy-checkout", {
                pack_key: packKey,
                origin_url: window.location.origin,
            });
            toast.info("Redirecting to secure checkout…");
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e));
            setBusy(null);
        }
    };

    const startCardSetup = async () => {
        setArBusy(true);
        try {
            const { data } = await api.post("/credits/auto-recharge/setup-checkout", {
                origin_url: window.location.origin,
            });
            toast.info("Redirecting to secure card setup…");
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e));
            setArBusy(false);
        }
    };

    const saveArConfig = async (patch) => {
        const next = { ...ar.auto_recharge, ...patch };
        setArBusy(true);
        try {
            const { data } = await api.post("/credits/auto-recharge", next);
            setAr({ ...ar, auto_recharge: data.auto_recharge });
            toast.success("Auto-recharge updated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setArBusy(false);
        }
    };

    const disableAr = async () => {
        setArBusy(true);
        try {
            await api.delete("/credits/auto-recharge");
            setAr({ ...ar, auto_recharge: { ...ar.auto_recharge, enabled: false } });
            toast.success("Auto-recharge disabled");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setArBusy(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="credits-page">
            <div>
                <p className="overline">Prepaid Mailings</p>
                <h1 className="font-serif text-4xl sm:text-5xl text-[#0F1E3D] mt-1">Mailing Credits</h1>
                <p className="text-sm text-slate-500 mt-2 max-w-2xl">
                    Buy credits upfront and skip the per-letter checkout. Credits apply to every USPS mailing — Standard,
                    Certified, or Certified + Return Receipt — at Lob&apos;s real cost plus a flat processing fee.
                </p>
            </div>

            <Card className="p-6 bg-gradient-to-br from-[#0F1E3D] to-[#1a2f5c] text-white" data-testid="credit-balance-card">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <p className="overline text-slate-200">Current balance</p>
                        <p className="font-mono text-4xl mt-1" data-testid="balance-usd">
                            ${(balance / 100).toFixed(2)}
                        </p>
                    </div>
                    <Coins size={48} weight="duotone" className="text-amber-300 opacity-80" />
                </div>
                <p className="text-xs text-slate-300 mt-3">
                    Credits never expire. Auto-applied when you mail a letter — Stripe only opens if your balance is too low.
                </p>
            </Card>

            {polling && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900" data-testid="credits-polling">
                    <span className="animate-spin inline-block h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                    Confirming your purchase with Stripe — usually a few seconds.
                </div>
            )}

            <div className="grid md:grid-cols-3 gap-4" data-testid="credit-packs">
                {packs.map((p) => {
                    const bonus = p.credit_usd - p.price_usd;
                    return (
                        <Card key={p.key} className="p-6 relative" data-testid={`credit-pack-${p.key}`}>
                            {p.bonus_pct >= 10 && (
                                <Badge variant="outline" className="absolute top-4 right-4 gap-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                                    <Sparkle size={11} weight="fill" /> Best value
                                </Badge>
                            )}
                            <p className="overline">{p.label}</p>
                            <p className="font-mono text-3xl text-[#0F1E3D] mt-2">${p.price_usd}</p>
                            <p className="text-sm text-slate-600 mt-1">
                    You get <strong>${p.credit_usd}</strong> of mailing credit
                            </p>
                            <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700">
                                <CheckCircle size={12} weight="fill" />
                                Bonus: ${bonus} ({p.bonus_pct}% extra)
                            </div>
                            <Button
                                className="w-full mt-5"
                                onClick={() => buy(p.key)}
                                disabled={busy === p.key}
                                data-testid={`buy-pack-${p.key}`}
                            >
                                {busy === p.key ? "Redirecting…" : `Buy for $${p.price_usd}`}
                            </Button>
                        </Card>
                    );
                })}
            </div>

            <Card className="p-6" data-testid="autorecharge-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                        <Lightning size={22} weight={ar.auto_recharge?.enabled ? "fill" : "regular"} className={ar.auto_recharge?.enabled ? "text-emerald-600 mt-1" : "text-slate-400 mt-1"} />
                        <div>
                            <h3 className="font-semibold text-slate-900">Auto-recharge</h3>
                            <p className="text-xs text-slate-500 mt-0.5 max-w-lg">
                                Automatically buy a credit pack when your balance drops below a threshold. Uses a saved card — hands-off.
                            </p>
                        </div>
                    </div>
                    {ar.has_card && (
                        <Switch
                            checked={!!ar.auto_recharge?.enabled}
                            onCheckedChange={(v) => v ? saveArConfig({ enabled: true }) : disableAr()}
                            disabled={arBusy}
                            data-testid="autorecharge-switch"
                        />
                    )}
                </div>

                {!ar.has_card ? (
                    <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded">
                        <CreditCard size={20} className="text-slate-500 shrink-0" />
                        <p className="text-sm text-slate-700 flex-1">
                            Save a card first — we&apos;ll charge it only when your balance falls below your threshold.
                        </p>
                        <Button size="sm" onClick={startCardSetup} disabled={arBusy} data-testid="autorecharge-add-card">
                            {arBusy ? "Redirecting…" : "Add payment method"}
                        </Button>
                    </div>
                ) : (
                    <div className="mt-4 space-y-4">
                        <div className="flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded text-sm" data-testid="autorecharge-saved-card">
                            <CreditCard size={18} className="text-slate-600" />
                            <span className="text-slate-700">
                                <strong className="uppercase">{ar.card?.brand}</strong> •• {ar.card?.last4}
                                <span className="text-slate-400"> · exp {String(ar.card?.exp_month).padStart(2, "0")}/{String(ar.card?.exp_year).slice(-2)}</span>
                            </span>
                            <button onClick={startCardSetup} className="ml-auto text-xs text-slate-600 underline hover:text-[#0F1E3D]" data-testid="autorecharge-change-card">
                                Change card
                            </button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs">When balance falls below</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="100"
                                    step="1"
                                    value={((ar.auto_recharge?.threshold_cents || 500) / 100).toString()}
                                    onChange={(e) => {
                                        const dollars = Math.max(1, Math.min(100, Math.round(Number(e.target.value) || 0)));
                                        setAr({ ...ar, auto_recharge: { ...ar.auto_recharge, threshold_cents: dollars * 100 } });
                                    }}
                                    onBlur={() => saveArConfig({ threshold_cents: ar.auto_recharge?.threshold_cents || 500 })}
                                    className="mt-1"
                                    data-testid="autorecharge-threshold"
                                />
                                <p className="text-[10px] text-slate-500 mt-1">Dollars (min $1, max $100)</p>
                            </div>
                            <div>
                                <Label className="text-xs">Refill with</Label>
                                <Select
                                    value={ar.auto_recharge?.pack_key || "starter"}
                                    onValueChange={(v) => saveArConfig({ pack_key: v })}
                                >
                                    <SelectTrigger className="mt-1" data-testid="autorecharge-pack-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {packs.map((p) => (
                                            <SelectItem key={p.key} value={p.key}>{p.label} — ${p.price_usd} → ${p.credit_usd}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {!ar.auto_recharge?.enabled && (
                            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2">
                                <XCircle size={12} />
                                Auto-recharge is currently OFF. Flip the switch above to enable.
                            </div>
                        )}
                    </div>
                )}
            </Card>

            <div className="border-t border-slate-200 pt-6 text-xs text-slate-500 space-y-2">
                <p><strong>How it works.</strong> Each USPS mailing costs postage + a $0.75 flat processing fee ($2.30 Standard, $7.60 Certified, $10.25 Certified + Return Receipt). Credits are debited at send time; if you don&apos;t have enough, you&apos;ll be routed to a normal Stripe checkout for that mailing only.</p>
                <p>Credits are non-refundable but never expire. See your usage in Case → mailing history.</p>
            </div>
        </div>
    );
}
